import React from "react";

import {
  StyleSheet,
  View,
  Text,
  Dimensions,
  ScrollView,
  KeyboardAvoidingView,
  TouchableOpacity,
} from "react-native";

import Utils, {
  hasItems,
  getStateData,
  navigate,
  escapeRegexp,
  sortByRelevance,
  isBaseCoreElement,
  isWeb,
} from "../../../../helpers/utils";

import Header from "../../common/header";
import AppConfig from "../../../../config/AppConfig";
import ActivitiesList from "../ActivitiesList/ActivitiesList";
import ManaTextInput from "../../common/ManaTextInput";
import ImageComponent from "../../common/ImageComponent";
import ManaEmojiPicker from "../../common/emoji-picker/ManaEmojiPicker";
import TagsList from "../../tags/TagsList/TagsList";
import Search from "../../common/search-bar/Search";
import appStyles from "../../../appStyles";
import BottomButton from "../../common/BottomButton";
import EditTagFabButton from "../../tags/EditTagFabButton";

const { height: HEIGHT, width: WIDTH } = Dimensions.get("window");

export default class EditSetPage extends React.Component {
  constructor(props) {
    super(props);
    this.controller = props.controller;
    this.timeout = 0;

    this.state = {
      isLoading: true,
      set: {},
      allTags: [],
      allActivities: [],
      openEmojiDialog: false,
      message: "",
    };
  }

  componentDidMount = async () => {
    if (this.controller.hasNavigation()) {
      this.focusListenerUnsub = this.props.navigation.addListener(
        "focus",
        () => {
          this.refreshPage();
        },
      );
    } else {
      this.refreshPage();
    }
  };

  componentWillUnmount = async () => {
    if (this.focusListenerUnsub) this.focusListenerUnsub();
  };

  getArgs() {
    if (this.controller.hasNavigation()) {
      return this.props.route.params?.args;
    } else {
      return this.controller.state.screenArgs?.args?.args;
    }
  }

  refreshPage = async () => {
    const { set } = this.getArgs() || {};

    if (!set) {
      this.controller.changePage("feed", {
        selectedPage: "rn:activities",
        args: {},
      });
      return;
    }

    // TAGS
    const allTags = this.getTags(set);
    const selectedTags = allTags.filter((t) => t.isSelected);

    // ACTIVITIES
    const allActivities = this.getActivities(set);
    const selectedActivitiesIds = allActivities
      .filter((a) => a.is_selected)
      .map((a) => a.id);

    this.setState((state) => ({
      ...state,
      isLoading: false,
      set,
      allTags,
      selectedTags,
      allActivities,
      selectedActivitiesIds: new Set(selectedActivitiesIds),
      openEmojiDialog: false,
      isBaseSet: isBaseCoreElement(set.edit_name),
      isEditMode: !!set.edit_id,
    }));
  };

  getTags = (set) => {
    const allTags = getStateData(this.controller.state, "tags").map((t) => {
      return {
        ...t,
        isSelected: (t.sets || []).includes(set.edit_id),
      };
    });

    return allTags;
  };

  getActivities = (set) => {
    let allActivities = getStateData(this.controller.state, "activities")
      .filter((a) => a.is_active)
      .map((a) => {
        return {
          ...a,
          is_favorite: a.is_favorite || 0,
          is_selected: (a.sets || []).includes(set.edit_id),
        };
      });

    allActivities.sort((a, b) => {
      if (a.is_selected == b.is_selected) {
        if (a.is_favorite == b.is_favorite) {
          return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
        }
        return a.is_favorite < b.is_favorite ? 1 : -1;
      }

      return a.is_selected > b.is_selected ? -1 : 1;
    });
    return allActivities;
  };

  // ------------------------------------- UPDATE -------------------------------------
  updateModel = (key, value) => {
    if (!key) return;

    const model = { ...this.state.set, [key]: value };
    this.setState(
      (state) => ({ ...state, set: model }),
      () => {
        // Auto save
        this.saveCoreElement();
      },
    );
  };

  updateSelectedActivities = (activity, is_selected) => {
    const { selectedActivitiesIds } = this.state;

    if (is_selected) {
      selectedActivitiesIds.add(activity.id);
    } else {
      selectedActivitiesIds.delete(activity.id);
    }

    this.setState(
      (state) => ({
        ...state,
        selectedActivitiesIds,
      }),
      () => {
        // Auto save
        this.saveCoreElement();
      },
    );
  };

  onRemoveTag = (tag) => {
    const { allTags, selectedTags } = this.state;

    let tags = allTags.map((t) => {
      return {
        ...t,
        isSelected: t.id == tag.id ? false : t.isSelected,
      };
    });

    this.setState(
      (state) => ({
        ...state,
        selectedTags: selectedTags.filter((t) => t.id !== tag.id),
        allTags: tags,
      }),
      () => {
        // Auto save
        this.saveCoreElement();
      },
    );
  };

  onSelectTag = (tag) => {
    const { selectedTags, allTags } = this.state;

    const tags = allTags.map((t) => {
      return {
        ...t,
        isSelected: t.id == tag.id ? true : t.isSelected,
      };
    });

    this.setState(
      (state) => ({
        ...state,
        selectedTags: [...selectedTags, { ...tag, isSelected: true }],
        allTags: tags,
      }),
      () => {
        // Auto save
        this.saveCoreElement();
      },
    );
  };

  // ------------------------------------- SAVE -------------------------------------
  isValidCoreElement = () => {
    const { set, selectedActivitiesIds } = this.state;

    return set.edit_name && hasItems([...selectedActivitiesIds]);
  };

  saveCoreElement = (userTriggered = false) => {
    const { set, selectedTags, selectedActivitiesIds, isEditMode } = this.state;

    // On create page save is triggered by the user
    if (!isEditMode && !userTriggered) return;

    if (!this.isValidCoreElement()) {
      this.setState((state) => ({
        ...state,
        isInvalid: true,
        message: "Core Element name & selected activities are mandatory!",
      }));

      setTimeout(() => {
        this.setState((state) => ({ ...state, message: "" }));
      }, 3000);
      return;
    }

    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));

    if (this.timeout) clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this.controller.tracking.track("Edit Set");
      console.log("Edit Set");
      this.controller.cardActionWithCallback(
        {
          action: "save",
          target: "set",
          ...set,
          activities: [...selectedActivitiesIds],
          tags: selectedTags.map((t) => t.id),
        },
        (data, response) => this.onSaveComplete(response),
      );
    }, 1000);
  };

  onSaveComplete = (response) => {
    const { isEditMode } = this.state;

    this.setState((state) => ({ ...state, isLoading: false }));

    if (isEditMode) return;

    const returnUrl = this.controller.getReturnUrl();
    // If coming from reflection edit view: add new set to view and go back
    if (returnUrl == "sets_reflection" && response?.id) {
      this.saveSelectedSetToReflectionView(response?.id, returnUrl);
    } else {
      this.goBack();
    }
  };

  saveSelectedSetToReflectionView = (setId, returnUrl) => {
    const action = {
      action: "save",
      target: "set_view_entry",
      id: setId,
      selected: 1,
      view: "Default",
    };

    console.log("Save View Entry");
    this.controller.cardActionWithCallback(action, () => {
      navigate(this.controller, returnUrl, "changepage", {
        show_edit_sets_view: true,
      });
    });
  };

  // ------------------------------------- EMOJI -------------------------------------
  toggleEmojiDialog = (isOpen = true) => {
    if (this.state.isBaseSet) return;
    this.setState((state) => ({ ...state, openEmojiDialog: isOpen }));
  };

  goBack = () => {
    if (this.controller.hasNavigation() && this.controller.navigation) {
      this.controller.goBack();
      return;
    }

    const returnUrl = this.controller.getReturnUrl();
    navigate(this.controller, returnUrl, "changepage");
  };

  // ------------------------------------- FILTERING -------------------------------------
  filterResults = ({ term }) => {
    const { allTags, allActivities } = this.state;

    // Multi terms search
    const pattern = Utils.getSearchPattern(term);
    let filteredTags = allTags.map((t) => {
      return {
        ...t,
        isHidden: term ? t.name && !t.name.match(pattern) : false,
      };
    });

    const filteredActivities = allActivities.map((t) => {
      return {
        ...t,
        isHidden: term ? t.name && !t.name.match(pattern) : false,
      };
    });

    this.setState((state) => ({
      ...state,
      allTags: sortByRelevance(filteredTags, term),
      allActivities: filteredActivities,
    }));
  };

  render() {
    const {
      allActivities,
      selectedTags,
      allTags,
      isLoading,
      set,
      openEmojiDialog,
      isBaseSet,
      isEditMode,
    } = this.state;

    const availableTags = allTags.filter((t) => !t.isHidden && !t.isSelected);
    const availableActivities = allActivities.filter((t) => !t.isHidden);

    const containerHeight = isEditMode
      ? isWeb
        ? HEIGHT
        : HEIGHT - 10
      : HEIGHT - 65;

    return (
      <View style={appStyles.container}>
        <ManaEmojiPicker
          isOpen={openEmojiDialog}
          onConfirm={(emoji) => this.updateModel("edit_icon", emoji?.unified)}
          onClose={() => this.toggleEmojiDialog(false)}
        />
        <KeyboardAvoidingView
          style={{ height: containerHeight }}
          behavior="height"
        >
          <Header
            title={set.edit_id ? "Edit Core Element" : "New Core Element"}
            subTitle="Choose tags and activities to include"
            controller={this.controller}
            goBack={this.goBack}
            hideAvatar
            isLoading={isLoading}
          />

          <View style={styles.content}>
            <View style={styles.inputContainer}>
              <TouchableOpacity
                style={[styles.imageContainer]}
                onPress={() => this.toggleEmojiDialog(true)}
              >
                <ImageComponent
                  styles={styles.icon}
                  icon={set && set.edit_icon}
                />
              </TouchableOpacity>

              <ManaTextInput
                readOnly={isBaseSet}
                style={{ paddingLeft: 50 }}
                placeholderTextColor="#9C9EB9"
                name={"tagNameEdit"}
                placeholder="Core Element Name"
                onTextChanged={(text) => this.updateModel("edit_name", text)}
                value={set.edit_name || ""}
              />
            </View>

            <ScrollView contentContainerStyle={styles.scrollView}>
              {hasItems(selectedTags) ? (
                <TagsList
                  title="Tags"
                  tags={selectedTags}
                  hideCollapse
                  showRemove
                  onRemoveTag={this.onRemoveTag}
                  initialNumToRender={5}
                />
              ) : null}

              {hasItems(availableTags) ? (
                <TagsList
                  hideCollapse
                  tags={availableTags}
                  onSelectTag={this.onSelectTag}
                  initialNumToRender={AppConfig.NUM_OF_TAGS}
                />
              ) : null}

              {hasItems(availableActivities) ? (
                <ActivitiesList
                  title="Activities"
                  contentType="activities"
                  activities={availableActivities}
                  initialNumToRender={10}
                  controller={this.controller}
                  onActivitySelected={this.updateSelectedActivities}
                  showSelector
                />
              ) : null}
              <Text style={{ marginBottom: 60 }}> </Text>
            </ScrollView>
          </View>

          <EditTagFabButton
            mariOptions={{
              mariMessage: this.state.message,
            }}
          />

          <Search
            placeholder="Search Activities & Tags"
            filterResults={this.filterResults}
          />
        </KeyboardAvoidingView>

        {!isEditMode ? (
          <View style={appStyles.footerOptions}>
            <BottomButton
              disabled={!this.isValidCoreElement()}
              buttonText="Create Core Element"
              onPress={() => this.saveCoreElement(true)}
            />
          </View>
        ) : null}
      </View>
    );
  }
}

const styles = StyleSheet.create({
  content: {
    backgroundColor: "#FFF",
    borderTopLeftRadius: AppConfig.BORDER_RADIUS,
    borderTopRightRadius: AppConfig.BORDER_RADIUS,
    flex: 1,
  },
  scrollView: {
    paddingBottom: 80,
    paddingHorizontal: 10,
  },
  footerOptions: {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
  },
  inputContainer: {
    width: WIDTH,
    maxWidth: "100%",
    marginTop: 10,
    paddingHorizontal: 10,
    display: "flex",
    justifyContent: "center",
    flexDirection: "row",
  },

  imageContainer: {
    position: "absolute",
    left: 10,
    top: 0,
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: "#FFF",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 100,
  },
  icon: {
    width: 30,
    height: 30,
  },
});
